/**
 * Übersetzt und ordnet die Bausteine des MJML-Plugins.
 *
 * Das Plugin liefert seine Bausteine auf Englisch („1 Column", „Group Social")
 * und ohne Symbole. In einer Kanzlei arbeitet niemand mit „Spacer" — und ein
 * Baustein, dessen Name man erst übersetzen muss, wird nicht benutzt.
 *
 * Einige Bausteine werden ganz entfernt: Was in einem Newsletter einer
 * Steuerberatung nichts verloren hat oder wofür es einen besseren
 * Kanzlei-Baustein gibt, steht sonst nur im Weg.
 */

import { symbolFuer, SYMBOLE } from './baustein-symbole.js';

/**
 * Deutsche Namen für die Bausteine des Plugins.
 * Schlüssel sind die Kennungen, die das Plugin vergibt.
 * @type {Record<string, string>}
 */
const NAMEN = {
  'mj-1-column': 'Abschnitt, eine Spalte',
  'mj-2-columns': 'Abschnitt, zwei Spalten',
  'mj-3-columns': 'Abschnitt, drei Spalten',
  // Diese vier heissen fast wie die Kanzlei-Bausteine, sind aber etwas
  // anderes: sie kommen IN eine Spalte, waehrend die Kanzlei-Bausteine ganze
  // Abschnitte sind. Ohne den Zusatz stehen zwei Bausteine namens "Bild"
  // untereinander, und der obere laesst sich nicht in eine Spalte setzen.
  'mj-text': 'Text in Spalte',
  'mj-button': 'Schaltfläche in Spalte',
  'mj-image': 'Bild in Spalte',
  'mj-divider': 'Trennlinie in Spalte',
  'mj-spacer': 'Abstand',
  'mj-social-group': 'Soziale Netzwerke',
  'mj-social-element': 'Netzwerk-Symbol',
  'mj-navbar': 'Menüleiste',
  'mj-navbar-link': 'Menüpunkt',
  'mj-hero': 'Aufmacher',
  'mj-wrapper': 'Umschlag',
  'mj-section': 'Abschnitt',
  'mj-column': 'Spalte',
  'mj-table': 'Tabelle',
  'mj-raw': 'Eigener HTML-Code',
};

/**
 * Bausteine, die aus der Auswahl verschwinden.
 *
 * Menüleisten und Netzwerk-Symbole gehören in einen Newsletter einer
 * Steuerberatung nicht hinein; „Eigener HTML-Code" umgeht das Prüf-Gate und
 * ist damit genau der Weg, auf dem Outlook-Fehler wieder hereinkommen.
 */
const ENTFERNEN = new Set([
  'mj-navbar',
  'mj-navbar-link',
  'mj-social-group',
  'mj-social-element',
  'mj-hero',
  'mj-raw',
  'mj-wrapper',
]);

/** Symbole für die verbleibenden Plugin-Bausteine. */
const PLUGIN_SYMBOLE = {
  'mj-1-column': 'kanzlei-absatz',
  'mj-2-columns': 'kanzlei-zwei-spalten',
  'mj-text': 'kanzlei-absatz',
  'mj-button': 'kanzlei-schaltflaeche',
  'mj-image': 'kanzlei-bild',
  'mj-divider': 'kanzlei-trenner',
};

/**
 * Räumt die Bausteinauswahl auf: übersetzt, bebildert, entfernt Überflüssiges.
 *
 * @param {any} editor
 */
export function bausteineAufraeumen(editor) {
  const verwaltung = editor.BlockManager;

  const alle = [...verwaltung.getAll().models];

  // Reihenfolge der Gruppen: Die Auswahl zeigt sie in der Reihenfolge, in der
  // die Kategorien zuerst gebraucht werden. Deshalb ZUERST die Kanzlei-
  // Bausteine - sie sind der Regelfall, und ein fertiger Abschnitt ist für
  // die tägliche Arbeit die richtige Wahl.
  for (const baustein of alle) {
    if (!baustein.getId().startsWith('kanzlei-')) continue;
    baustein.set('category', { id: 'kanzlei', label: 'Fertige Abschnitte', open: true, order: 1 });
  }

  for (const baustein of alle) {
    const kennung = baustein.getId();
    if (kennung.startsWith('kanzlei-')) continue;

    if (ENTFERNEN.has(kennung)) {
      verwaltung.remove(kennung);
      continue;
    }

    const name = NAMEN[kennung];
    if (name) baustein.set('label', name);

    if (!baustein.get('media')) {
      const vorbild = PLUGIN_SYMBOLE[/** @type {keyof typeof PLUGIN_SYMBOLE} */ (kennung)];
      baustein.set('media', symbolFuer(vorbild ?? kennung));
    }

    // Eigene Kategorie unter den Kanzlei-Bausteinen - aber AUFGEKLAPPT.
    // Zugeklappt fand sie niemand, und es sind genau die Bausteine, die in
    // eine einzelne Spalte passen: ohne sie bekommt man rechts neben einen
    // Text kein Bild.
    baustein.set('category', {
      id: 'grundelemente',
      label: 'Einzelne Inhalte (in eine Spalte)',
      open: true,
      order: 2,
    });
  }

  // Sicherstellen, dass für jeden Kanzlei-Baustein ein Symbol vorliegt.
  for (const kennung of Object.keys(SYMBOLE)) {
    const baustein = verwaltung.get(kennung);
    if (baustein && !baustein.get('media')) baustein.set('media', symbolFuer(kennung));
  }

  // Neu zeichnen. Ohne diesen Schritt bleibt die Auswahl auf dem Stand von
  // vor dem Umsortieren: die Bausteine der zweiten Gruppe hängen dann ohne
  // Überschrift unter den Kanzlei-Bausteinen, und niemand erkennt, dass es
  // zwei verschiedene Arten sind.
  verwaltung.render(verwaltung.getAll().models);
}
